/*
 * Multi-buy offer: the shopper's progress towards the store's automatic discounts, read from the real cart.
 * Tiers come from snippets/hl-offer-tiers (data-tiers="2:5,3:10"); Shopify applies the best tier reached, counted
 * on every piece in the cart. Used by the collection offer band (sections/hl-offer) and the quick-add modal
 * (assets/hl-quick-add.js) through window.HLOffer. Nothing here changes prices: it only describes them.
 */
import { StandardEvents } from '@shopify/events';

const parseTiers = (s) =>
  String(s || '2:5,3:10').split(',').map((t) => t.split(':').map(Number)).filter((t) => t[0] > 0 && t[1] > 0)
    .map(([q, p]) => ({ q, p })).sort((a, b) => a.q - b.q);

let cart = null, pending = null;
const listeners = new Set();

function load() {
  if (pending) return pending;
  pending = fetch('/cart.js', { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .then((c) => { if (c) { cart = { count: c.item_count, subtotal: c.original_total_price, currency: c.currency }; listeners.forEach((f) => f(cart)); } return cart; })
    .catch(() => cart)
    .finally(() => { pending = null; });
  return pending;
}

/** The tier a cart of `count` pieces gets, and the next one up */
function tierFor(tiers, count) {
  let now = null, next = null;
  for (const t of tiers) { if (count >= t.q) now = t; else if (!next) next = t; }
  return { now, next };
}

const piece = (n) => n + (n === 1 ? ' piece' : ' pieces');

/** One short sentence about the cart as it is now (no item being added) */
function cartLine(tiers, count) {
  const { now, next } = tierFor(tiers, count);
  if (!count) return '';
  if (now && !next) return `<strong>${now.p}% off</strong> is on: ${piece(count)} in your cart.`;
  if (now) return `${piece(count)} in your cart: <strong>${now.p}% off</strong>. Add ${next.q - count} more for ${next.p}%.`;
  return `${piece(count)} in your cart. Add ${next.q - count} more to save ${next.p}%.`;
}

window.HLOffer = {
  parseTiers, tierFor, piece, cartLine,
  get cart() { return cart; },
  load,
  /** Calls fn(cart) now (if known) and after every cart change */
  watch(fn) { listeners.add(fn); if (cart) fn(cart); return () => listeners.delete(fn); },
};

// Any add, update or removal anywhere on the page (product form, quick add, cards, drawer)
document.addEventListener(StandardEvents.cartLinesUpdate, (e) => {
  const done = () => setTimeout(load, 50);
  if (e.promise && e.promise.then) e.promise.then(done, done); else done();
});
window.addEventListener('pageshow', (e) => { if (e.persisted) load(); });

/* ---------- collection band: [data-hl-offer] ---------- */
document.querySelectorAll('[data-hl-offer]').forEach((band) => {
  const tiers = parseTiers(band.dataset.tiers), status = band.querySelector('[data-hl-offer-status]');
  const tierEls = band.querySelectorAll('[data-tier]');
  window.HLOffer.watch((c) => {
    const { now } = tierFor(tiers, c.count);
    tierEls.forEach((el) => el.classList.toggle('is-reached', !!now && Number(el.dataset.tier) === now.q));
    if (!status) return;
    const line = cartLine(tiers, c.count);
    status.innerHTML = line;
    status.hidden = !line;
  });
});

load();
